import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Switch } from '../components/ui/switch';
import { toast } from 'react-hot-toast';
import { Search, Edit, Save, X, MapPin, DollarSign, Settings, Store, Users, QrCode, Download, Copy } from 'lucide-react';
import { supabase } from '../lib/supabase/client/supabaseClient';
import QRCode from 'qrcode';

interface Shop {
  id: string;
  name: string;
  address: string;
  location: string;
  city: string;
  postal_code: number;
  lat: number;
  lon: number;
  contact_info: string;
  KYC: boolean;
  is_online: boolean;
  speed: number;
  cover_page_mode: string;
  shop_username: string;
  shop_image_path?: string;
}

interface PageCosting {
  id: string;
  shop_id: string;
  paper_type: string;
  user_color_cost: number;
  user_monochrome_cost: number;
  shop_color_cost: number;
  shop_monochrome_cost: number;
}

interface ShopManagementPageProps {
  isEmbedded?: boolean;
}

const ShopManagementPage: React.FC<ShopManagementPageProps> = ({ isEmbedded = false }) => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedShopId, setSelectedShopId] = useState<string>('');
  const [editingShop, setEditingShop] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Shop>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingShop, setUpdatingShop] = useState<string | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [showQrCode, setShowQrCode] = useState(false);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [pricing, setPricing] = useState<PageCosting[]>([]);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [selectedPaperType, setSelectedPaperType] = useState<string>('A4');
  const [editingPricing, setEditingPricing] = useState(false);
  const [pricingForm, setPricingForm] = useState<Partial<PageCosting>>({});

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    if (selectedShopId) {
      fetchShopPricing(selectedShopId);
    } else {
      setPricing([]);
    }
  }, [selectedShopId]);

  const fetchShopPricing = async (shopId: string) => {
    setLoadingPricing(true);
    try {
      const { data, error } = await supabase.schema('api')
        .rpc('fetch_shop_with_pricing', { p_shop_id: shopId });

      if (error) throw error;
      
      if (data?.success && data?.data?.pricing) {
        setPricing(data.data.pricing);
      } else {
        setPricing([]);
      }
    } catch (error: any) {
      console.error('Error fetching shop pricing:', error);
      toast.error('Failed to load pricing data');
      setPricing([]);
    } finally {
      setLoadingPricing(false);
    }
  };

  const fetchShops = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('print_shops')
        .select('*')
        .order('name');

      if (error) throw error;
      setShops(data || []);
    } catch (error: any) {
      console.error('Error fetching shops:', error);
      toast.error('Failed to load shops');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (shop: Shop) => {
    setEditingShop(shop.id);
    setEditForm({ ...shop });
  };

  const handleCancel = () => {
    setEditingShop(null);
    setEditForm({});
    setEditingPricing(false);
    setPricingForm({});
  };

  const handleSave = async (shopId: string) => {
    if (!editForm) return;

    setUpdatingShop(shopId);
    try {
      // Update using RPC (updates both shop and pricing if provided)
      const { data, error: rpcError } = await supabase.schema('api')
        .rpc('update_shop_with_pricing', {
          p_shop_id: shopId,
          p_shop_data: editForm,
          p_pricing_data: pricing.length > 0 ? pricing : null
        });

      if (rpcError) throw rpcError;
      if (!data?.success) throw new Error(data?.message || 'Update failed');

      // Update Redis cache via backend endpoint - only send editable fields
      const cacheUpdateData = {
        name: editForm.name,
        address: editForm.address,
        city: editForm.city,
        postal_code: editForm.postal_code,
        lat: editForm.lat,
        lon: editForm.lon,
        contact_info: editForm.contact_info,
        KYC: editForm.KYC,
        speed: editForm.speed,
        cover_page_mode: editForm.cover_page_mode,
      };
      
      console.log('Sending to backend cache update:', cacheUpdateData);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/shops/${shopId}/update-cache`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cacheUpdateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update cache');
      }

      // Update pricing cache if pricing data exists
      if (pricing.length > 0) {
        const pricingByPaperType = pricing.reduce((acc, p) => {
          acc[p.paper_type] = {
            user_color_cost: p.user_color_cost,
            user_monochrome_cost: p.user_monochrome_cost,
            shop_color_cost: p.shop_color_cost,
            shop_monochrome_cost: p.shop_monochrome_cost
          };
          return acc;
        }, {} as any);

        const pricingResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/shops/${shopId}/update-pricing-cache`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pricing: pricingByPaperType }),
        });

        if (!pricingResponse.ok) {
          console.warn('Failed to update pricing cache, but shop data was updated');
        } else {
          const pricingResult = await pricingResponse.json();
          console.log('Pricing cache update result:', pricingResult);
        }
      }

      // Update local state
      setShops(prev => prev.map(shop => 
        shop.id === shopId ? { ...shop, ...editForm } as Shop : shop
      ));

      setEditingShop(null);
      setEditForm({});
      toast.success('Shop updated successfully');
    } catch (error: any) {
      console.error('Error updating shop:', error);
      toast.error(`Failed to update shop: ${error.message}`);
    } finally {
      setUpdatingShop(null);
    }
  };

  const handleEditPricing = () => {
    const currentPricing = pricing.find(p => p.paper_type === selectedPaperType);
    if (currentPricing) {
      setPricingForm({ ...currentPricing });
    } else {
      setPricingForm({
        shop_id: selectedShopId,
        paper_type: selectedPaperType,
        user_color_cost: 0,
        user_monochrome_cost: 0,
        shop_color_cost: 0,
        shop_monochrome_cost: 0
      });
    }
    setEditingPricing(true);
  };

  const handleSavePricing = () => {
    if (!pricingForm.paper_type) return;

    const existingIndex = pricing.findIndex(p => p.paper_type === pricingForm.paper_type);
    if (existingIndex >= 0) {
      const updated = [...pricing];
      updated[existingIndex] = { ...updated[existingIndex], ...pricingForm } as PageCosting;
      setPricing(updated);
    } else {
      setPricing([...pricing, pricingForm as PageCosting]);
    }
    setEditingPricing(false);
    setPricingForm({});
    toast.success('Pricing updated locally. Click Save to apply changes.');
  };

  const handleCancelPricing = () => {
    setEditingPricing(false);
    setPricingForm({});
  };

  const handleImageUpload = async (file: File | undefined) => {
    if (!file || !selectedShopId) return;

    const shop = shops.find(s => s.id === selectedShopId);
    if (!shop) return;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${shop.id}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('shop_images')
        .upload(filePath, file);

      if (uploadError) {
        toast.error('Failed to upload image');
        console.error(uploadError);
        return;
      }

      const { error: updateError } = await supabase
        .from('print_shops')
        .update({ shop_image_path: filePath })
        .eq('id', shop.id);

      if (updateError) {
        toast.error('Failed to update shop image path');
        console.error(updateError);
        return;
      }

      setShops(shops.map(s => 
        s.id === shop.id 
          ? { ...s, shop_image_path: filePath }
          : s
      ));

      toast.success('Shop image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    }
  };

  const handleRemoveImage = async () => {
    if (!selectedShopId) return;
    
    const shop = shops.find(s => s.id === selectedShopId);
    if (!shop || !shop.shop_image_path) return;

    try {
      const { error: deleteError } = await supabase.storage
        .from('shop_images')
        .remove([shop.shop_image_path]);

      if (deleteError) {
        toast.error('Failed to delete image from storage');
        console.error(deleteError);
        return;
      }

      const { error: updateError } = await supabase
        .from('print_shops')
        .update({ shop_image_path: null })
        .eq('id', shop.id);

      if (updateError) {
        toast.error('Failed to remove shop image path');
        console.error(updateError);
        return;
      }

      setShops(shops.map(s => 
        s.id === shop.id 
          ? { ...s, shop_image_path: undefined }
          : s
      ));

      toast.success('Shop image removed successfully');
    } catch (error) {
      console.error('Error removing image:', error);
      toast.error('Failed to remove image');
    }
  };

  const generateQRCode = async (shopId: string) => {
    setGeneratingQr(true);
    try {
      const qrUrl = `https://ctrlp.co.in/get-print/shop/${shopId}`;
      const qrDataUrl = await QRCode.toDataURL(qrUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(qrDataUrl);
      setShowQrCode(true);
      toast.success('QR Code generated successfully!');
    } catch (error: any) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    } finally {
      setGeneratingQr(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeDataUrl || !selectedShop) return;
    
    const link = document.createElement('a');
    link.download = `${selectedShop.name.replace(/\s+/g, '_')}_QR_Code.png`;
    link.href = qrCodeDataUrl;
    link.click();
  };

  const copyQRLink = async () => {
    if (!selectedShop) return;
    
    const qrUrl = `https://ctrlp.co.in/get-print/shop/${selectedShop.id}`;
    try {
      await navigator.clipboard.writeText(qrUrl);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const filteredShops = shops.filter(shop =>
    shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.shop_username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedShop = shops.find(shop => shop.id === selectedShopId);

  if (!isEmbedded) {
    return (
      <div className="container mx-auto px-4 py-6">
        <ShopManagementContent 
          shops={filteredShops}
          loading={loading}
          selectedShopId={selectedShopId}
          selectedShop={selectedShop}
          editingShop={editingShop}
          editForm={editForm}
          searchQuery={searchQuery}
          updatingShop={updatingShop}
          qrCodeDataUrl={qrCodeDataUrl}
          showQrCode={showQrCode}
          generatingQr={generatingQr}
          pricing={pricing}
          loadingPricing={loadingPricing}
          selectedPaperType={selectedPaperType}
          editingPricing={editingPricing}
          pricingForm={pricingForm}
          setSelectedShopId={setSelectedShopId}
          setSearchQuery={setSearchQuery}
          setEditForm={setEditForm}
          setShowQrCode={setShowQrCode}
          setSelectedPaperType={setSelectedPaperType}
          setPricingForm={setPricingForm}
          handleEdit={handleEdit}
          handleCancel={handleCancel}
          handleSave={handleSave}
          handleEditPricing={handleEditPricing}
          handleSavePricing={handleSavePricing}
          handleCancelPricing={handleCancelPricing}
          handleImageUpload={handleImageUpload}
          handleRemoveImage={handleRemoveImage}
          generateQRCode={generateQRCode}
          downloadQRCode={downloadQRCode}
          copyQRLink={copyQRLink}
        />
      </div>
    );
  }

  return (
    <ShopManagementContent 
      shops={filteredShops}
      loading={loading}
      selectedShopId={selectedShopId}
      selectedShop={selectedShop}
      editingShop={editingShop}
      editForm={editForm}
      searchQuery={searchQuery}
      updatingShop={updatingShop}
      qrCodeDataUrl={qrCodeDataUrl}
      showQrCode={showQrCode}
      generatingQr={generatingQr}
      pricing={pricing}
      loadingPricing={loadingPricing}
      selectedPaperType={selectedPaperType}
      editingPricing={editingPricing}
      pricingForm={pricingForm}
      setSelectedShopId={setSelectedShopId}
      setSearchQuery={setSearchQuery}
      setEditForm={setEditForm}
      setShowQrCode={setShowQrCode}
      setSelectedPaperType={setSelectedPaperType}
      setPricingForm={setPricingForm}
      handleEdit={handleEdit}
      handleCancel={handleCancel}
      handleSave={handleSave}
      handleEditPricing={handleEditPricing}
      handleSavePricing={handleSavePricing}
      handleCancelPricing={handleCancelPricing}
      handleImageUpload={handleImageUpload}
      handleRemoveImage={handleRemoveImage}
      generateQRCode={generateQRCode}
      downloadQRCode={downloadQRCode}
      copyQRLink={copyQRLink}
    />
  );
};

interface ShopManagementContentProps {
  shops: Shop[];
  loading: boolean;
  selectedShopId: string;
  selectedShop?: Shop;
  editingShop: string | null;
  editForm: Partial<Shop>;
  searchQuery: string;
  updatingShop: string | null;
  qrCodeDataUrl: string;
  showQrCode: boolean;
  generatingQr: boolean;
  pricing: PageCosting[];
  loadingPricing: boolean;
  selectedPaperType: string;
  editingPricing: boolean;
  pricingForm: Partial<PageCosting>;
  setSelectedShopId: (id: string) => void;
  setSearchQuery: (query: string) => void;
  setEditForm: (form: Partial<Shop>) => void;
  setShowQrCode: (show: boolean) => void;
  setSelectedPaperType: (type: string) => void;
  setPricingForm: (form: Partial<PageCosting>) => void;
  handleEdit: (shop: Shop) => void;
  handleCancel: () => void;
  handleSave: (shopId: string) => void;
  handleEditPricing: () => void;
  handleSavePricing: () => void;
  handleCancelPricing: () => void;
  handleImageUpload: (file: File | undefined) => void;
  handleRemoveImage: () => void;
  generateQRCode: (shopId: string) => void;
  downloadQRCode: () => void;
  copyQRLink: () => void;
}

const ShopManagementContent: React.FC<ShopManagementContentProps> = ({
  shops,
  loading,
  selectedShopId,
  selectedShop,
  editingShop,
  editForm,
  searchQuery,
  updatingShop,
  qrCodeDataUrl,
  showQrCode,
  generatingQr,
  pricing,
  loadingPricing,
  selectedPaperType,
  editingPricing,
  pricingForm,
  setSelectedShopId,
  setSearchQuery,
  setEditForm,
  setShowQrCode,
  setSelectedPaperType,
  setPricingForm,
  handleEdit,
  handleCancel,
  handleSave,
  handleEditPricing,
  handleSavePricing,
  handleCancelPricing,
  handleImageUpload,
  handleRemoveImage,
  generateQRCode,
  downloadQRCode,
  copyQRLink,
}) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Shop Management</h2>
          <p className="text-gray-600">Manage shop details, pricing, and settings</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Shop Selection Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Select Shop
              </CardTitle>
              <CardDescription>
                Choose a shop to view and edit its details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="relative w-full max-w-md">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search shops by name, city, or username..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Shop Dropdown */}
              <div className="space-y-2">
                <Label>Select Shop</Label>
                <Select
                  value={selectedShopId}
                  onValueChange={setSelectedShopId}
                >
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Choose a shop to manage">
                      {selectedShop ? (
                        <div className="flex items-center gap-2">
                          <Store className="h-4 w-4" />
                          <span>{selectedShop.name}</span>
                          <span className="text-sm text-gray-500">({selectedShop.city})</span>
                          {selectedShop.is_online && (
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                          )}
                        </div>
                      ) : (
                        "Choose a shop to manage"
                      )
                    }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {shops.length > 0 ? (
                      shops.map((shop) => (
                        <SelectItem key={shop.id} value={shop.id}>
                          <div className="flex items-center gap-2 w-full">
                            <Store className="h-4 w-4" />
                            <span>{shop.name}</span>
                            <span className="text-sm text-gray-500">({shop.city})</span>
                            {shop.is_online && (
                              <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>
                        No shops found matching your search
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Shop Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900">Total Shops</h4>
                  <p className="text-2xl font-bold text-blue-600">{shops.length}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-green-900">Online Shops</h4>
                  <p className="text-2xl font-bold text-green-600">
                    {shops.filter(shop => shop.is_online).length}
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h4 className="font-medium text-orange-900">KYC Verified</h4>
                  <p className="text-2xl font-bold text-orange-600">
                    {shops.filter(shop => shop.KYC).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selected Shop Details */}
          {selectedShop && (
            <Card className="overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {editingShop === selectedShop.id ? (
                        <Input
                          value={editForm.name || ''}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="text-lg font-semibold"
                        />
                      ) : (
                        <>
                          <Store className="h-5 w-5" />
                          {selectedShop.name}
                          {selectedShop.is_online && (
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                          )}
                        </>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {editingShop === selectedShop.id ? (
                        <Input
                          value={editForm.shop_username || ''}
                          onChange={(e) => setEditForm({ ...editForm, shop_username: e.target.value })}
                          placeholder="Shop username"
                        />
                      ) : (
                        `@${selectedShop.shop_username || 'No username'}`
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {editingShop === selectedShop.id ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleSave(selectedShop.id)}
                          disabled={updatingShop === selectedShop.id}
                        >
                          <Save className="h-4 w-4 mr-1" />
                          {updatingShop === selectedShop.id ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancel}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateQRCode(selectedShop.id)}
                          disabled={generatingQr}
                        >
                          <QrCode className="h-4 w-4 mr-1" />
                          {generatingQr ? 'Generating...' : 'Generate QR'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(selectedShop)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Location Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Location Details
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <Label className="text-sm text-gray-600">Address</Label>
                        {editingShop === selectedShop.id ? (
                          <Input
                            value={editForm.address || ''}
                            onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                          />
                        ) : (
                          <p className="text-sm">{selectedShop.address || 'N/A'}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-sm text-gray-600">City</Label>
                          {editingShop === selectedShop.id ? (
                            <Input
                              value={editForm.city || ''}
                              onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                            />
                          ) : (
                            <p className="text-sm">{selectedShop.city || 'N/A'}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-sm text-gray-600">Postal Code</Label>
                          {editingShop === selectedShop.id ? (
                            <Input
                              type="number"
                              value={editForm.postal_code || ''}
                              onChange={(e) => setEditForm({ ...editForm, postal_code: parseInt(e.target.value) || 0 })}
                            />
                          ) : (
                            <p className="text-sm">{selectedShop.postal_code || 'N/A'}</p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-sm text-gray-600">Latitude</Label>
                          {editingShop === selectedShop.id ? (
                            <Input
                              type="number"
                              step="any"
                              value={editForm.lat || ''}
                              onChange={(e) => setEditForm({ ...editForm, lat: parseFloat(e.target.value) || 0 })}
                            />
                          ) : (
                            <p className="text-sm">{selectedShop.lat?.toFixed(6) || 'N/A'}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-sm text-gray-600">Longitude</Label>
                          {editingShop === selectedShop.id ? (
                            <Input
                              type="number"
                              step="any"
                              value={editForm.lon || ''}
                              onChange={(e) => setEditForm({ ...editForm, lon: parseFloat(e.target.value) || 0 })}
                            />
                          ) : (
                            <p className="text-sm">{selectedShop.lon?.toFixed(6) || 'N/A'}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Section */}
                  <div className="space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Pricing Configuration
                    </h4>
                    
                    {loadingPricing ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Paper Type Selector */}
                        <div className="flex items-center gap-4">
                          <Label className="text-sm text-gray-600">Paper Type</Label>
                          <Select
                            value={selectedPaperType}
                            onValueChange={setSelectedPaperType}
                            disabled={editingPricing || editingShop !== selectedShop.id}
                          >
                            <SelectTrigger className="w-[180px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="A4">A4</SelectItem>
                              <SelectItem value="A3">A3</SelectItem>
                              <SelectItem value="Letter">Letter</SelectItem>
                              <SelectItem value="Legal">Legal</SelectItem>
                            </SelectContent>
                          </Select>
                          {editingShop === selectedShop.id && !editingPricing && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleEditPricing}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit {selectedPaperType} Pricing
                            </Button>
                          )}
                        </div>

                        {/* Pricing Display/Edit */}
                        {editingPricing ? (
                          <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-sm text-gray-600">User BW Cost (₹/page)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={pricingForm.user_monochrome_cost || ''}
                                  onChange={(e) => setPricingForm({ ...pricingForm, user_monochrome_cost: parseFloat(e.target.value) || 0 })}
                                />
                              </div>
                              <div>
                                <Label className="text-sm text-gray-600">User Color Cost (₹/page)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={pricingForm.user_color_cost || ''}
                                  onChange={(e) => setPricingForm({ ...pricingForm, user_color_cost: parseFloat(e.target.value) || 0 })}
                                />
                              </div>
                              <div>
                                <Label className="text-sm text-gray-600">Shop BW Earning (₹/page)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={pricingForm.shop_monochrome_cost || ''}
                                  onChange={(e) => setPricingForm({ ...pricingForm, shop_monochrome_cost: parseFloat(e.target.value) || 0 })}
                                />
                              </div>
                              <div>
                                <Label className="text-sm text-gray-600">Shop Color Earning (₹/page)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={pricingForm.shop_color_cost || ''}
                                  onChange={(e) => setPricingForm({ ...pricingForm, shop_color_cost: parseFloat(e.target.value) || 0 })}
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={handleSavePricing}>
                                <Save className="h-4 w-4 mr-1" />
                                Save Pricing
                              </Button>
                              <Button size="sm" variant="outline" onClick={handleCancelPricing}>
                                <X className="h-4 w-4 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          (() => {
                            const currentPricing = pricing.find(p => p.paper_type === selectedPaperType);
                            return currentPricing ? (
                              <div className="border rounded-lg p-4 space-y-2">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="text-sm text-gray-600">User BW Cost</Label>
                                    <p className="text-sm font-medium">₹{currentPricing.user_monochrome_cost?.toFixed(2) || '0.00'}/page</p>
                                  </div>
                                  <div>
                                    <Label className="text-sm text-gray-600">User Color Cost</Label>
                                    <p className="text-sm font-medium">₹{currentPricing.user_color_cost?.toFixed(2) || '0.00'}/page</p>
                                  </div>
                                  <div>
                                    <Label className="text-sm text-gray-600">Shop BW Earning</Label>
                                    <p className="text-sm font-medium">₹{currentPricing.shop_monochrome_cost?.toFixed(2) || '0.00'}/page</p>
                                  </div>
                                  <div>
                                    <Label className="text-sm text-gray-600">Shop Color Earning</Label>
                                    <p className="text-sm font-medium">₹{currentPricing.shop_color_cost?.toFixed(2) || '0.00'}/page</p>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="border border-dashed rounded-lg p-4 text-center text-gray-500">
                                <p className="text-sm">No pricing configured for {selectedPaperType}</p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleEditPricing}
                                  className="mt-2"
                                >
                                  Add Pricing
                                </Button>
                              </div>
                            );
                          })()
                        )}
                        <p className="text-xs text-gray-500">
                          <strong>Note:</strong> Changes are staged locally. Click "Save" button at the top to apply all changes to the database and Redis cache.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Settings Section */}
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Shop Settings
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-gray-600">KYC Verified</Label>
                      {editingShop === selectedShop.id ? (
                        <Switch
                          checked={editForm.KYC || false}
                          onCheckedChange={(checked: any) => setEditForm({ ...editForm, KYC: checked })}
                        />
                      ) : (
                        <span className={`text-sm px-2 py-1 rounded ${selectedShop.KYC ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {selectedShop.KYC ? 'Verified' : 'Not Verified'}
                        </span>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Speed (pages/min)</Label>
                      {editingShop === selectedShop.id ? (
                        <Input
                          type="number"
                          value={editForm.speed || ''}
                          onChange={(e) => setEditForm({ ...editForm, speed: parseInt(e.target.value) || 0 })}
                        />
                      ) : (
                        <p className="text-sm">{selectedShop.speed || 0} pages/min</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Cover Page Mode</Label>
                      {editingShop === selectedShop.id ? (
                        <Select
                          value={editForm.cover_page_mode || '0'}
                          onValueChange={(value) => setEditForm({ ...editForm, cover_page_mode: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">1 cover page</SelectItem>
                            <SelectItem value="1">Blank cover page</SelectItem>
                            <SelectItem value="2">No cover page</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm">
                          {selectedShop.cover_page_mode === '0' ? '1 cover page' :
                           selectedShop.cover_page_mode === '1' ? 'Blank cover page' :
                           selectedShop.cover_page_mode === '2' ? 'No cover page' : 'Unknown'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Shop Image Section */}
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Store className="h-4 w-4" />
                    Shop Image
                  </h4>
                  <div className="border rounded-lg p-4 space-y-4">
                    {/* Current Image Display */}
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        {selectedShop.shop_image_path ? (
                          <img
                            src={`${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/shop_images/${selectedShop.shop_image_path}`}
                            alt="Shop image"
                            className="w-20 h-20 object-cover rounded-lg border"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              ((e.target as HTMLImageElement).nextElementSibling as HTMLElement).style.display = 'flex';
                            }}
                          />
                        ) : null}
                        {!selectedShop.shop_image_path && (
                          <div className="w-20 h-20 bg-gray-100 rounded-lg border flex items-center justify-center">
                            <Store className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-2">
                          {selectedShop.shop_image_path ? 'Current shop image' : 'No image uploaded'}
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e.target.files?.[0])}
                            className="hidden"
                            id="shopImageUpload"
                          />
                          <label
                            htmlFor="shopImageUpload"
                            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer"
                          >
                            {selectedShop.shop_image_path ? 'Change Image' : 'Upload Image'}
                          </label>
                          {selectedShop.shop_image_path && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRemoveImage()}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* QR Code Dialog */}
          <Dialog open={showQrCode} onOpenChange={setShowQrCode}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <QrCode className="h-5 w-5" />
                  Shop QR Code
                </DialogTitle>
                <DialogDescription>
                  Scan this QR code to quickly access the shop's print page
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col items-center space-y-4 py-4">
                {qrCodeDataUrl && (
                  <>
                    <img 
                      src={qrCodeDataUrl} 
                      alt="Shop QR Code" 
                      className="border border-gray-200 rounded-lg bg-white p-2 max-w-[250px]"
                    />
                    <div className="text-center space-y-3 w-full">
                      <div className="bg-gray-50 p-3 rounded-md">
                        <p className="text-sm text-gray-600 break-all">
                          <span className="font-semibold">Link:</span><br />
                          <span className="font-mono text-xs">https://ctrlp.co.in/get-print/shop/{selectedShop?.id}</span>
                        </p>
                      </div>
                      <div className="flex gap-2 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={downloadQRCode}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={copyQRLink}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* No Shop Selected Message */}
          {!selectedShop && shops.length > 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Please select a shop from the dropdown above to view and edit its details.</p>
              </CardContent>
            </Card>
          )}

          {/* No Shops Found Message */}
          {shops.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No shops found matching your search criteria.</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ShopManagementPage;